// Admin: preview an uploaded PDF / doc lesson or its attached resources.
// Uses an <iframe> for PDFs (browsers render natively) via the authenticated
// /api/files/ proxy which sets Content-Disposition: inline. Other doc types
// fall back to a download link (also through /api/files/).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Download, X } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/lib/api";

// Route storage-hosted paths through the authenticated `/api/files/` proxy so
// the browser gets the correct Content-Type + `Content-Disposition: inline`.
// Absolute URLs and already-proxied `/api/…` paths are passed through as-is.
const toFileUrl = (p) => {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  if (p.startsWith("/api/")) {
    const backend = API_BASE.replace(/\/api$/, "");
    return `${backend}${p}`;
  }
  // Raw storage key → hit the authenticated proxy
  const key = p.replace(/^\/+/, "");
  return `${API_BASE}/files/${key}`;
};

export function AdminDocumentPreviewDialog({ open, onOpenChange, lesson }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const primaryUrl = lesson?.content_file_path ? toFileUrl(lesson.content_file_path) : "";
  const primaryIsPdf = (lesson?.lesson_type || "").toLowerCase() === "pdf";

  useEffect(() => {
    if (!open || !lesson?.id) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/admin/lessons/${lesson.id}/resources`);
        setResources(data.data?.resources || []);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [open, lesson?.id]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4" data-testid="admin-doc-preview-modal">
      <div className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-3">
          <div className="min-w-0 flex items-center gap-2 text-sm font-bold text-slate-900">
            <FileText className="h-4 w-4 text-[#101876]" />
            <span className="truncate">{lesson?.lesson_title || "Document preview"}</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-600">{lesson?.lesson_type}</span>
          </div>
          <button type="button" onClick={() => onOpenChange(false)} data-testid="admin-doc-preview-close"
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden bg-slate-50">
          {primaryUrl && primaryIsPdf ? (
            <iframe src={primaryUrl} title="pdf-preview" className="h-full w-full" data-testid="admin-doc-iframe" />
          ) : primaryUrl ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-sm text-slate-600">
              <FileText className="h-10 w-10 text-slate-400" />
              <p>Preview isn&apos;t supported for this file type. Download it to view.</p>
              <a href={primaryUrl} download className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c]">
                <Download className="h-3.5 w-3.5" /> Download primary file
              </a>
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-sm text-slate-500">
              <FileText className="h-10 w-10 text-slate-300" />
              <p>No primary document uploaded for this lesson yet.</p>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 bg-white p-3">
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Attached resources ({resources.length})
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-[11px] text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> loading…</div>
          ) : resources.length === 0 ? (
            <div className="text-[11px] text-slate-400">None</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {resources.map((r) => (
                <a key={r.id} href={toFileUrl(r.file_path)} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
                   data-testid={`admin-doc-res-${r.id}`}>
                  <FileText className="h-3 w-3" /> {r.resource_title || r.original_filename}
                  <span className="text-[9px] uppercase text-slate-400">{r.resource_type}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
